import React, { useEffect, useMemo, useRef } from 'react'
import injectSheet from 'react-jss'
import NewSlideButton from './new-slide-button/new-slide-button'
import classNames from 'classnames'
import { isTouchScreen } from 'pobble-components/dist/utils'
import { Presentation } from 'pobble-components/dist/presentation'
import ButtonElement from 'pobble-components/dist/button-element'
import { useDispatch } from 'react-redux'
import { setActiveSlideId } from '../redux/builder.actions'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash'

const Sortableholder = SortableContainer(
  ({ children, componentRef, className }) => {
    return (
      <div ref={componentRef} className={className}>
        {children}
      </div>
    )
  }
)
const SortableComponent = SortableElement(props => <div {...props} />)

const slidePreviewStyles = ({ COLORS, FONT_SIZES }) => ({
  container: {
    position: 'relative'
  },
  button: {
    width: '100%',
    borderRadius: '4px',
    marginBottom: '20px',
    overflow: 'hidden',
    border: '1px solid rgba(0, 0, 0, 0.16)',
    position: 'relative',
    '& > * > *': {
      pointerEvents: 'none'
    }
  },
  active: {
    borderColor: COLORS.BLUE.NORMAL
  },
  hidden: {
    '& > div': {
      opacity: '.4'
    }
  },
  iconHidden: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginLeft: '-12px',
    marginTop: '-12px',
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    fontSize: '24px',
    zIndex: '100'
  },
  order: {
    position: 'absolute',
    right: '100%',
    marginRight: '8px',
    ...FONT_SIZES.BODY_DEFAULT.STYLE,
    color: COLORS.TEXT.MID_GREY.NORMAL
  }
})

const SlidePreview = injectSheet(slidePreviewStyles)(
  ({
    classes,
    active,
    id,
    setActiveSlide,
    hidden,
    children,
    order,
    ...props
  }) => {
    const dispatch = useDispatch()
    return (
      <div className={classes.container}>
        <span className={classes.order}>{order}</span>
        <ButtonElement
          onMouseDown={() => dispatch(setActiveSlideId(id))}
          className={classNames(
            classes.button,
            `metrics__builder_slide_preview_${order}`,
            {
              [classes.active]: active,
              [classes.hidden]: hidden
            }
          )}
          {...props}>
          <>
            {hidden && (
              <FontAwesomeIcon
                className={classes.iconHidden}
                icon={faEyeSlash}
              />
            )}
            {children}
          </>
        </ButtonElement>
      </div>
    )
  }
)

const styles = ({ COLORS }) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  slide: {
    marginBottom: '20px'
  },
  draggedPreview: {
    transition: 'box-shadow 0.3s',
    boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.1)'
  },
  disabledPreview: {
    opacity: '0.5'
  },
  newSlideButtonContainer: {
    padding: '0 20px 0 32px'
  },
  newSlideButtonHolder: {
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(0, 0, 0, .1)'
  },
  slidePreviewList: {
    height: '100%',
    overflow: 'auto',
    position: 'relative',
    padding: '11px 20px 0 32px'
  }
})

const SlidePreviewList = ({
  classes,
  slides,
  className,
  onSortEnd,
  onSortStart,
  isDragging,
  activeSlideId,
  presentation
}) => {
  const container = useRef(null)

  const dispatch = useDispatch()

  const upToDatePresentation = useMemo(
    () => {
      return !presentation
        ? null
        : {
            ...presentation,
            slides: slides
          }
    },
    [presentation, slides]
  )

  const activeSlideIndex = useMemo(
    () => {
      for (let i = 0; i < slides.length; i++) {
        if (slides[i].id === activeSlideId) {
          return i
        }
      }
    },
    [activeSlideId, slides]
  )

  /**
   * Scroll to active preview to keep it visible
   */
  useEffect(
    () => {
      const selectedSlide = container.current.children[activeSlideIndex]
      if (selectedSlide.offsetTop - container.current.scrollTop < 0) {
        container.current.scrollTop = selectedSlide.offsetTop
      } else if (
        selectedSlide.offsetTop - container.current.scrollTop + 210 >
        container.current.offsetHeight
      ) {
        container.current.scrollTop =
          selectedSlide.offsetTop - container.current.offsetHeight + 225
      }
    },
    [slides, activeSlideId, activeSlideIndex, isDragging]
  )

  // Keyboard navigation
  useEffect(
    () => {
      const listener = e => {
        const element = e.target
        const key = e.key
        if (
          !element.getAttribute('contenteditable') &&
          element.tagName !== 'TEXTAREA' &&
          element.tagName !== 'INPUT'
        ) {
          switch (key) {
            case 'ArrowDown':
            case 'ArrowRight':
              if (activeSlideIndex < slides.length - 1) {
                dispatch(setActiveSlideId(slides[activeSlideIndex + 1].id))
              }
              break
            case 'ArrowUp':
            case 'ArrowLeft':
              if (activeSlideIndex > 0) {
                dispatch(setActiveSlideId(slides[activeSlideIndex - 1].id))
              }
              break
            default:
              break
          }
        }
      }
      document.addEventListener('keydown', listener)
      return () => document.removeEventListener('keydown', listener)
    },
    [slides, dispatch, activeSlideIndex]
  )

  return (
    <div className={classes.container}>
      <div className={classes.newSlideButtonContainer}>
        <div className={classes.newSlideButtonHolder}>
          <NewSlideButton />
        </div>
      </div>
      <Sortableholder
        pressDelay={!isTouchScreen ? undefined : 100} // Press to hold for touch screens
        distance={isTouchScreen ? undefined : 1}
        componentRef={container}
        className={classNames(classes.slidePreviewList, className)}
        onSortStart={onSortStart}
        onSortEnd={onSortEnd}>
        <Presentation
          thumbnail
          slideWidth={226}
          data={upToDatePresentation}
          multiSlides>
          {props => {
            return props.slides.map((slide, index) => {
              const slideData = slides[index]
              const preview = (
                <SlidePreview
                  order={index + 1}
                  hidden={slideData.hidden}
                  active={activeSlideId === slideData.id}
                  id={slideData.id}>
                  {slide}
                </SlidePreview>
              )
              return index < 2 ? (
                <div key={index}>{preview}</div>
              ) : (
                <SortableComponent id={index} key={index} index={index}>
                  {preview}
                </SortableComponent>
              )
            })
          }}
        </Presentation>
      </Sortableholder>
    </div>
  )
}

export default injectSheet(styles)(SlidePreviewList)
